import React, { useEffect, useState } from 'react';
import type { QuickSuggestion } from '@constants/onboardingFeatures';
import './OnboardingFeatureGrid.scss';

interface OnboardingFeatureGridProps {
  suggestions?: QuickSuggestion[];
  maxVisible?: number;
  isLoading?: boolean;
  isThinking?: boolean;
  /** Bump on chatbot close → collapse "Xem thêm" */
  resetKey?: number;
  onSuggestionClick: (suggestion: QuickSuggestion) => void;
}

const DEFAULT_MAX_VISIBLE = 4;

const OnboardingFeatureGrid: React.FC<OnboardingFeatureGridProps> = ({
  suggestions = [],
  maxVisible = DEFAULT_MAX_VISIBLE,
  isLoading = false,
  isThinking = false,
  resetKey = 0,
  onSuggestionClick,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    setIsExpanded(false);
  }, [resetKey]);

  // Keep parent order (API order, or pinned tip first). Do not re-sort by `order`.
  const displayCommands = suggestions;

  const collapsedCount = maxVisible > 0 ? maxVisible : DEFAULT_MAX_VISIBLE;
  const visibleCommands = isExpanded
    ? displayCommands
    : displayCommands.slice(0, collapsedCount);

  const shouldShowToggle = displayCommands.length > collapsedCount;

  if (isLoading && suggestions.length === 0) {
    return (
      <div className="onboarding-feature-grid">
        <div className="onboarding-feature-grid__list">
          {Array.from({ length: collapsedCount }).map((_, index) => (
            <div
              key={`skeleton-${index}`}
              className="h-[174px] max-h-[174px] w-full overflow-hidden rounded-[24px] border border-[#f2f2f4] bg-[linear-gradient(90deg,#f7f7f8_25%,#ececf0_50%,#f7f7f8_75%)] bg-[length:200%_100%] shadow-[0px_2px_16px_0px_#0000000F] pointer-events-none"
            />
          ))}
        </div>
      </div>
    );
  }

  if (displayCommands.length === 0) {
    return null;
  }

  return (
    <div className="onboarding-feature-grid">
      <div className="onboarding-feature-grid__list">
        {visibleCommands.map((suggestion, index) => {
          const title = suggestion.title || `Tính năng ${index + 1}`;
          const description = suggestion.description || '';
          const illustrationSrc = suggestion.imageUrl;

          return (
            <button
              key={suggestion.featureKey || `${title}-${index}`}
              type="button"
              className={`h-[174px] max-h-[174px] w-full overflow-hidden rounded-[24px] border border-[#f2f2f4] bg-white p-0 text-left shadow-[0px_2px_16px_0px_#0000000F] transition-all hover:bg-[var(--colour-semantic-neutral-surface)] ${isThinking ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
              onClick={() => {
                if (!isThinking) {
                  onSuggestionClick(suggestion);
                }
              }}
              disabled={isThinking}
            >
              <div className="onboarding-feature-card__content">
                <div className="onboarding-feature-card__text">
                  <div className="font-sans text-base font-medium leading-6 text-[#242428]">{title}</div>
                  {description && (
                    <div className="min-h-[40px] font-sans text-sm font-normal leading-5 text-[#94949E]">{description}</div>
                  )}
                </div>

                <div className="onboarding-feature-card__illustration">
                  {illustrationSrc ? (
                    <img
                      src={illustrationSrc}
                      alt={title}
                      className="onboarding-feature-card__image"
                    />
                  ) : (
                    <div className="onboarding-feature-card__fallback">
                      {title.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {shouldShowToggle && !isExpanded && (
        <div className="onboarding-feature-grid__action">
          <button
            type="button"
            className="onboarding-feature-grid__toggle"
            onClick={() => setIsExpanded(true)}
          >
            Xem thêm
          </button>
        </div>
      )}
    </div>
  );
};

export default OnboardingFeatureGrid;
